import React, { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useApiListProvider, useApiListProviderHealth } from '../../../../../api-new/state/auth'
import { IconLink, IconProvider, IconView } from '../../../../../shared/icon/icon'
import { useRoleSysProvider } from '../../../../../role/hook/role-sys'
import { FleetPage } from '../../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu, ColumnSelector, ColumnText } from '../../../../../shared/table-builder/table-columns'
import { Menu, MenuDelete, MenuItem } from '../../../../../shared/menu/menu'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { connectionUse, ConnectionUsePill, isConnection } from '../../../../../shared/sso-workspace/connection-use'
import { ProviderLogo } from '../../../../../shared/sso-workspace/provider-logo'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { formatDate, plural, serverMessage } from '../../../../../shared/sso-workspace/format'
import { ProviderRollup } from './provider-rollup'
import { PROVIDER_SECTION_BREADCRUMB } from './provider-breadcrumb'
import { useAuditCapabilities } from '../../../../../shared/audit/use-audit-capabilities'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

const DAY = 24 * 60 * 60 * 1000

const PROTOCOL = { oidc: 'OpenID Connect' }
const REGISTRATION = { own_registration: 'own app registration', shared_app: "EducationXR's shared app" }

// What kind of connection a row is, in words (never the raw enums).
export const connectionKind = row => {
  const protocol = PROTOCOL[row.driver] || row.driver || ''
  const registration = row.driver === 'oidc' ? REGISTRATION[row.connection] : null
  return [protocol, registration].filter(Boolean).join(' · ')
}

// Who owns the connection: an organization, or EducationXR (platform-held)
// and how many organizations use it.
export const connectionOwner = row =>
  row.organizationName
    ? row.organizationName
    : `EducationXR${row.linkedOrganizationCount ? ` · used by ${plural(row.linkedOrganizationCount, 'organization')}` : ''}`

// The last health check's verdict (provider health rows are keyed by
// providerID). No row means the check hasn't reported on it yet.
export const healthVerdict = health => {
  if (!health) return { tone: null, label: 'Not checked', rank: 0 }
  if ((health.errors || []).length) return { tone: 'bad', label: 'Problem', rank: 3, detail: health.errors[0] }
  if (((health.discovery && health.discovery.warnings) || []).length) return { tone: 'warn', label: 'Warnings', rank: 2, detail: health.discovery.warnings[0] }
  return { tone: 'ok', label: 'Healthy', rank: 1 }
}

// The credential's expiry in words. A shared-app connection has no expiry
// EducationXR tracks here.
export const credentialState = (row, now = Date.now()) => {
  if (row.driver !== 'oidc' || row.connection === 'shared_app' || !row.expiresAt) return { label: '', sort: Infinity }
  const at = new Date(row.expiresAt).getTime()
  if (Number.isNaN(at)) return { label: '', sort: Infinity }
  const days = Math.ceil((at - now) / DAY)
  if (days <= 0) return { label: 'Expired', tone: 'bad', sort: days, detail: formatDate(row.expiresAt) }
  return { label: `${plural(days, 'day')} left`, tone: days <= 30 ? 'warn' : null, sort: days, detail: formatDate(row.expiresAt) }
}

const domainsSummary = domains => {
  if (!domains || !domains.length) return ''
  return domains.length > 2 ? `${domains.slice(0, 2).join(', ')} and ${domains.length - 2} more` : domains.join(', ')
}

// Not in use is what the old Sign-in methods page listed as "Not offered to
// anyone": switched on, no domain bound, so nobody is sent to it.
const STATUS_FILTERS = [
  { value: '', label: 'All connections' },
  { value: 'active', label: 'Active' },
  { value: 'not-in-use', label: 'Not in use' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'attention', label: 'Needs attention' }
]

const name = row => row.displayName || row.providerName

// Active, Not in use (switched on, not public, no domain bound: nobody is
// sent to it) or Inactive -- connection-use.js.
const statusOf = row => connectionUse({ active: row.active, isPublic: row.public, bound: Array.isArray(row.domains) ? row.domains.length : undefined })
const STATUS_RANK = { active: 0, 'not-in-use': 1, inactive: 2 }

// Every connection (spec 2026-09-24 vocabulary: a provider row is a
// "connection"), in the workspace's language: who owns it, what it is,
// the domains it signs in, its status, health and credential, with the
// platform rollup above. Delete asks first (audit F7: it was a one-click
// pinned action shown to everyone) and is only offered with delete access.
export const ProviderList = () => {
  const apiListProvider = useApiListProvider()
  const apiListProviderHealth = useApiListProviderHealth()
  const roleSysProvider = useRoleSysProvider()
  const caps = useAuditCapabilities()

  const [rows, setRows] = useState(undefined)
  const [health, setHealth] = useState({})
  const [statusFilter, setStatusFilter] = useState('')
  const [deleting, setDeleting] = useState(null)
  const [deleteError, deleteFailure, setDeleteError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('')

  // Sign-in methods' rows are the Sign-in methods page's, never listed here.
  const load = async () => {
    try {
      setRows(((await apiListProvider.get()) || []).filter(isConnection))
    } catch (err) {
      setRows(null)
    }
  }

  const loadHealth = async () => {
    try {
      const items = (await apiListProviderHealth.get()) || []
      const byID = {}
      items.forEach(item => { byID[item.providerID] = item })
      setHealth(byID)
    } catch (err) {
      setHealth({})
    }
  }

  const tableRef = useRef()

  // The grid's ready (and its footer's reload) fetches both lists.
  const loadAll = () => Promise.all([load(), loadHealth()])

  const needsAttention = row => {
    const verdict = healthVerdict(health[row.id])
    const credential = credentialState(row)
    return verdict.rank >= 2 || credential.tone === 'bad' || credential.tone === 'warn' || statusOf(row).key === 'not-in-use'
  }

  const visible = useMemo(() => {
    if (!Array.isArray(rows)) return rows
    switch (statusFilter) {
      case 'active': return rows.filter(r => statusOf(r).key === 'active')
      case 'not-in-use': return rows.filter(r => statusOf(r).key === 'not-in-use')
      case 'inactive': return rows.filter(r => !r.active)
      case 'attention': return rows.filter(needsAttention)
      default: return rows
    }
  }, [rows, statusFilter, health]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    setBusy(true)
    setDeleteError(null)
    try {
      await apiListProvider.delete(deleting.id)
    } catch (err) {
      setDeleteError(serverMessage(err, `${name(deleting)} couldn't be deleted.`), err)
      setBusy(false)
      return
    }
    setStatus(`Deleted ${name(deleting)}. It's in the recycle bin if you need it back.`)
    setBusy(false)
    setDeleting(null)
    await load()
  }

  // The cells' words, flattened onto the row: the grid sorts, quick-filters
  // and exports the cell VALUE, so each column's field holds what it shows.
  const gridRows = useMemo(
    () =>
      Array.isArray(visible)
        ? visible.map(row => {
          const verdict = healthVerdict(health[row.id])
          const credential = credentialState(row)
          const use = statusOf(row)
          return {
            ...row,
            name: name(row),
            owner: connectionOwner(row),
            kind: connectionKind(row),
            domainsText: (row.domains || []).join(' '),
            domainCount: (row.domains || []).length,
            use,
            statusRank: STATUS_RANK[use.key],
            statusText: [use.label, row.scim ? 'Provisioning' : ''].filter(Boolean).join(' '),
            healthLabel: verdict.label,
            healthRank: -verdict.rank,
            healthTone: verdict.tone,
            healthDetail: verdict.detail,
            credentialLabel: credential.label,
            credentialSort: credential.sort,
            credentialTone: credential.tone,
            credentialDetail: credential.detail
          }
        })
        : visible,
    [visible, health]
  )
  const gridList = useGridList(gridRows)

  return (
    <BreadcrumbView Icon={PROVIDER_SECTION_BREADCRUMB.Icon} text={PROVIDER_SECTION_BREADCRUMB.text}>
      <FleetPage
        tabs
        title="Connections"
        purpose="Every identity provider people sign in to EducationXR with: who owns it, the domains it signs in, and whether it's healthy."
        actions={
          <>
            {roleSysProvider.accessCreate && <Link className="btn sw-btn sw-btn-primary" to="/admin/sso/connections/add">Add connection</Link>}
            {roleSysProvider.accessCreate && <Link className="btn sw-btn" to="/admin/sso/connect">Connect Microsoft or Google</Link>}
            {roleSysProvider.accessDelete && <Link className="btn sw-btn" to="/admin/sso/connections/recycle">Recycle bin</Link>}
            {caps.sysAuditLog && <Link className="btn sw-btn" to="/admin/audit-log?type=providers">Audit log</Link>}
          </>
        }>
        <ProviderRollup />
        <div className="sw-toolbar">
          <select className="form-select form-select-sm" aria-label="Show" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {STATUS_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {status && <p className="sw-status" role="status" aria-live="polite">{status}</p>}
        </div>
        {rows === null && <p className="text-danger small" role="alert">The connections couldn't be loaded. Reload to try again.</p>}
        <FleetGrid title="connections" tableRef={tableRef} exportName="connections">
          <TableAgClient
            ref={tableRef}
            apiList={gridList}
            onReady={loadAll}
            sortCol="name"
            rowHeight={ROW_HEIGHT_TWO_LINE}
            emptyState={{
              Icon: IconProvider,
              title: 'No connections yet',
              message: "Connect an organization's Microsoft or Google sign-in, or add another identity provider, so people can sign in with the accounts they already use.",
              action: { label: 'Connect Microsoft or Google', Icon: IconLink, to: '/admin/sso/connect', show: Boolean(roleSysProvider.accessCreate) }
            }}
            noMatchState={{ message: 'No connections match. Clear the search or choose "All connections".' }}>
            <ColumnFormat field="name" header="Connection" render={connectionCell} stacked minWidth={240} flex={2} sortable />
            <ColumnText field="owner" header="Owner" sortable />
            <ColumnText field="kind" header="Kind" sortable />
            <ColumnSelector field="domainsText" header="Domains" selector={row => domainsCell({ data: row })} comparator={byField('domainCount')} sortable />
            <ColumnSelector field="statusText" header="Status" selector={row => statusCell({ data: row })} comparator={byField('statusRank')} sortable stacked minWidth={170} />
            <ColumnSelector field="healthLabel" header="Health" selector={row => healthCell({ data: row })} comparator={byField('healthRank')} sortable />
            <ColumnSelector field="credentialLabel" header="Credential" selector={row => credentialCell({ data: row })} comparator={byField('credentialSort')} sortable />
            <ColumnMenu
              menu={row =>
                <Menu small>
                  <MenuItem Icon={IconView} text="open" to={`/admin/sso/connections/${row.id}`} pin show />
                  <MenuDelete onClick={() => { setDeleteError(null); setDeleting(row) }} show={Boolean(roleSysProvider.accessDelete)} />
                </Menu>}
            />
          </TableAgClient>
        </FleetGrid>
      </FleetPage>

      <ConfirmDialog
        open={Boolean(deleting)}
        title={deleting ? `Delete ${name(deleting)}?` : ''}
        body={deleting
          ? `Nobody can sign in with ${name(deleting)} any more${(deleting.domains || []).length ? `, and ${deleting.domains.join(', ')} stop${deleting.domains.length === 1 ? 's' : ''} routing to it` : ''}. People already signed in stay signed in. You can restore it from the recycle bin.`
          : ''}
        confirmText="Delete connection"
        busy={busy}
        error={deleteError}
        failure={deleteFailure}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </BreadcrumbView>
  )
}

// Sort a column by another (numeric) field of the row rather than its label
// (ColumnSelector passes an AG Grid comparator through).
const byField = key => (a, b, nodeA, nodeB) => {
  const x = nodeA && nodeA.data ? nodeA.data[key] : 0
  const y = nodeB && nodeB.data ? nodeB.data[key] : 0
  return x === y ? 0 : x < y ? -1 : 1
}

const connectionCell = cell => {
  const row = cell.data
  if (!row) return ''
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <span className="d-none d-sm-inline-flex"><ProviderLogo iconSVG={row.iconSVG} iconSVGDark={row.iconSVGDark} /></span>
      <span className="min-w-0">
        <Link to={`/admin/sso/connections/${row.id}`} className="fw-semibold">{name(row)}</Link>
        {row.displayName && row.providerName && row.displayName !== row.providerName && <span className="sw-cell-sub">{row.providerName}</span>}
      </span>
    </span>
  )
}

const domainsCell = cell => {
  const row = cell.data
  if (!row) return ''
  return (row.domains || []).length
    ? <span title={(row.domains || []).join(', ')}>{domainsSummary(row.domains)}</span>
    : <span className="sw-hint">None bound</span>
}

// Auth's provider rows carry `scim` (spec R22): true while some linked
// organization has a live SCIM token for the connection. An older auth
// sends none, and no badge shows.
export const PROVISIONING_TITLE = "Provisioning (SCIM) is on: an organization's identity provider adds and removes its people"

// The status pill, and for a connection nobody is sent to, why (on up to two
// lines in the column; its title has it whole).
const statusCell = cell => {
  const row = cell.data
  if (!row) return ''
  const use = row.use || statusOf(row)
  return (
    <span className="min-w-0">
      <span className="d-inline-flex gap-1">
        <ConnectionUsePill use={use} />
        {row.scim && <Pill tone="info" title={PROVISIONING_TITLE}>Provisioning</Pill>}
      </span>
      {use.reason && <span className="sw-cell-sub tb-cell-wrap2" title={use.reason}>{use.reason}</span>}
    </span>
  )
}

const healthCell = cell => (cell.data ? <Pill tone={cell.data.healthTone} title={cell.data.healthDetail}>{cell.data.healthLabel}</Pill> : '')

const credentialCell = cell => {
  const row = cell.data
  if (!row) return ''
  return row.credentialLabel
    ? <span className={row.credentialTone ? `sw-tone-${row.credentialTone} fw-semibold` : undefined} title={row.credentialDetail}>{row.credentialLabel}</span>
    : <span className="sw-hint">Not tracked</span>
}
